/**
 * Workflow Schedule Types
 *
 * TypeScript types for scheduled workflow execution.
 */

import type { UserFilterOptions } from './imageWorkflow';

// ============================================================================
// Schedule Types
// ============================================================================

export type ScheduleType = 'once' | 'recurring' | 'cron';
export type RecurringInterval = 'hourly' | 'daily' | 'weekly' | 'monthly';
export type ScheduleStatus = 'active' | 'completed' | 'paused' | 'error';
export type TargetType = 'current_user' | 'all_valid_users' | 'specific_users';

/**
 * Runtime options to override agent behavior during workflow execution.
 * Used to prevent workflow execution from affecting user's real memory.
 */
export interface RuntimeOptions {
  /** If true, memory toolkit won't be added to agent tools */
  disable_memory_tool?: boolean;
  /** If true, conversation won't be saved to memory after chat */
  disable_memory_write?: boolean;
  /** Hook stages to disable (e.g., 'FINAL') */
  disabled_hook_stages?: string[];
}

// ============================================================================
// Schedule Config
// ============================================================================

export interface OnceScheduleConfig {
  run_at: string; // ISO 8601 datetime
}

export interface RecurringScheduleConfig {
  interval: RecurringInterval;
  time: string; // HH:MM format
  timezone: string;
  days_of_week?: number[]; // 0=Mon, 6=Sun
  days_of_month?: number[]; // 1-31
}

export interface CronScheduleConfig {
  expression: string; // 5-field cron expression
  timezone: string;
}

export type ScheduleConfig = OnceScheduleConfig | RecurringScheduleConfig | CronScheduleConfig;

// ============================================================================
// Prompt Config
// ============================================================================

export interface PromptConfig {
  agent_type?: string;
  model?: string;
  temperature?: number;
  system_prompt?: string;
}

// ============================================================================
// Schedule Models
// ============================================================================

export interface WorkflowSchedule {
  id: string;
  workflow_id: string;
  name: string;
  description?: string;
  created_by: string;

  schedule_type: ScheduleType;
  schedule_config: Record<string, unknown>;

  // Optional for backward compatibility with records created before migration
  target_type?: TargetType;
  target_user_ids: string[];
  /** Runtime options to override agent behavior */
  runtime_options?: RuntimeOptions;

  is_enabled: boolean;
  status: ScheduleStatus;

  next_run_at?: string;
  last_run_at?: string;
  last_execution_id?: string;

  total_runs: number;
  successful_runs: number;
  failed_runs: number;

  created_at: string;
  updated_at: string;

  workflow_name?: string;
}

// ============================================================================
// API Request/Response
// ============================================================================

export interface ScheduleCreateRequest {
  workflow_id: string;
  name: string;
  description?: string;
  schedule_type: ScheduleType;
  schedule_config: Record<string, unknown>;
  target_type?: TargetType;
  target_user_ids?: string[];
  /** User filter options (only used when target_type is 'all_valid_users') */
  user_filter?: UserFilterOptions;
  /** Runtime options to override agent behavior */
  runtime_options?: RuntimeOptions;
  is_enabled?: boolean;
}

export interface ScheduleUpdateRequest {
  name?: string;
  description?: string;
  schedule_type?: ScheduleType;
  schedule_config?: Record<string, unknown>;
  target_type?: TargetType;
  target_user_ids?: string[];
  /** User filter options (only used when target_type is 'all_valid_users') */
  user_filter?: UserFilterOptions;
  /** Runtime options to override agent behavior */
  runtime_options?: RuntimeOptions;
  is_enabled?: boolean;
}

export interface ScheduleListResponse {
  total: number;
  schedules: WorkflowSchedule[];
}

export interface ScheduleToggleResponse {
  id: string;
  is_enabled: boolean;
  status: ScheduleStatus;
  next_run_at?: string;
  message: string;
}

export interface ScheduleRunResponse {
  schedule_id: string;
  execution_id: string;
  status: string;
  message: string;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface ScheduleListParams {
  workflow_id?: string;
  status?: ScheduleStatus;
  is_enabled?: boolean;
  limit?: number;
  offset?: number;
}

// ============================================================================
// UI Helper Types
// ============================================================================

export interface ScheduleFormData {
  name: string;
  description: string;
  workflow_id: string;
  schedule_type: ScheduleType;

  // Once
  run_at: Date | null;

  // Recurring
  interval: RecurringInterval;
  time: string;
  timezone: string;
  days_of_week: number[];
  days_of_month: number[];

  // Cron
  cron_expression: string;

  // Execution settings
  target_user_ids: string[];
  runtime_options: RuntimeOptions;

  is_enabled: boolean;
}

export const DEFAULT_SCHEDULE_FORM: ScheduleFormData = {
  name: '',
  description: '',
  workflow_id: '',
  schedule_type: 'once',
  run_at: null,
  interval: 'daily',
  time: '10:00',
  timezone: 'Asia/Shanghai',
  days_of_week: [0, 1, 2, 3, 4], // Mon-Fri
  days_of_month: [1],
  cron_expression: '0 10 * * *',
  target_user_ids: [],
  runtime_options: {
    disable_memory_tool: true,
    disable_memory_write: true,
  },
  is_enabled: true,
};

// ============================================================================
// Constants
// ============================================================================

export const SCHEDULE_TYPE_LABELS: Record<ScheduleType, string> = {
  once: '一次性',
  recurring: '重复执行',
  cron: 'Cron表达式',
};

export const RECURRING_INTERVAL_LABELS: Record<RecurringInterval, string> = {
  hourly: '每小时',
  daily: '每天',
  weekly: '每周',
  monthly: '每月',
};

export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  active: '活跃',
  completed: '已完成',
  paused: '已暂停',
  error: '错误',
};

export const WEEKDAY_LABELS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Shanghai', label: '中国标准时间 (UTC+8)' },
  { value: 'Asia/Tokyo', label: '日本标准时间 (UTC+9)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: '美国东部时间' },
  { value: 'America/Los_Angeles', label: '美国太平洋时间' },
  { value: 'Europe/London', label: '英国时间' },
];
